import type { ValidationIssue } from '../types';
import { normalizeValidationId } from '../validationConstants';
import {
  hasReconnectGapMetadata,
  isReconnectGapMetadataAllowed,
} from './validationReconnectGapRules';

const CONNECTIVITY_RECONNECT_VALIDATION_IDS = new Set([21, 22]);
const CONNECTIVITY_RECONNECT_MESSAGE_PATTERN = /SYSTEM BREAK|DISCONNECTED/i;
const CONNECTOR_POSITION_RECONNECT_MESSAGE_PATTERN =
  /SYSTEM BREAK|DISCONNECTED|CONNECTED CONNECTORS HAVE POSITION MISMATCH|MULTI-CONNECTOR MISCONNECTION/i;
const GEOMETRY_RECONNECT_CANDIDATE_KEYS = [
  'reconnectCandidate',
  'Reconnect Candidate',
  'isReconnectCandidate',
  'isConnectorReconnectCandidate',
] as const;

export function isReconnectAllCandidateIssue(issue: ValidationIssue): boolean {
  if (issue.ElementIds.length < 2) {
    return false;
  }

  if (!isReconnectGapMetadataAllowed(issue.AdditionalData)) {
    return false;
  }

  const normalizedValidationId = normalizeValidationId(issue.ValidationId);
  const message = issue.Message || '';

  if (normalizedValidationId === 1 && CONNECTOR_POSITION_RECONNECT_MESSAGE_PATTERN.test(message)) {
    return true;
  }

  if (
    CONNECTIVITY_RECONNECT_VALIDATION_IDS.has(normalizedValidationId) &&
    CONNECTIVITY_RECONNECT_MESSAGE_PATTERN.test(message)
  ) {
    return true;
  }

  return (
    normalizedValidationId === 28 &&
    /GEOMETRIC OVERLAP/i.test(message) &&
    hasTruthyMetadataFlag(issue.AdditionalData, GEOMETRY_RECONNECT_CANDIDATE_KEYS) &&
    hasReconnectGapMetadata(issue.AdditionalData)
  );
}

export function getReconnectAllValidationId(issues: ValidationIssue[]): number | null {
  const reconnectableIssue = issues.find(isReconnectAllCandidateIssue);
  return reconnectableIssue ? normalizeValidationId(reconnectableIssue.ValidationId) : null;
}

function hasTruthyMetadataFlag(
  additionalData: Record<string, string> | undefined,
  keys: readonly string[]
): boolean {
  if (!additionalData) {
    return false;
  }

  return keys.some((key) => {
    const value = additionalData[key]?.trim().toLowerCase();
    return value === 'true' || value === '1' || value === 'yes';
  });
}
